import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MobileStickyBar from '../components/common/MobileStickyBar';
import { Check, ArrowRight, AlertTriangle } from 'lucide-react';

const CHECKLIST_PRO = [
  { cat: 'Technique & Son', items: ['Sono / système audio professionnel', 'Micros (filaires + sans fil)', 'Éclairage scène et salle', 'Rétroprojecteur / écran', 'Connexion internet haut débit'] },
  { cat: 'Accueil & Flux', items: ['Panneau de bienvenue', 'Badges nominatifs', 'Plan de la salle', 'Vestiaire organisé', 'Parking réservé'] },
  { cat: 'Animations', items: ['Photobooth brandé logo entreprise', 'Blind test ou quiz d\'équipe', 'Mini golf ou jeux bois', 'Animation culinaire', 'Tombola ou concours'] },
  { cat: 'Restauration', items: ['Cocktail dinatoire confirmé', 'Menu adapté régimes spéciaux', 'Boissons alcoolisées / soft', 'Service attentionné', 'Gâteau entreprise'] },
  { cat: 'Communication', items: ['Programme distribué', 'Photographe événementiel', 'Photos de groupe', 'Newsletter post-event', 'Retours feedback'] },
];

const ERREURS_PRO = [
  { e: 'Négliger la logistique technique', c: 'Tester toujours le son et la vidéo 2h avant. Les pannes arrivent souvent au dernier moment.' },
  { e: 'Réserver trop petit', c: 'Prévoir 20% de place supplémentaire pour la circulation. Les invités doivent se déplacer confortablement.' },
  { e: 'Manque de badges', c: 'Les badges facilitent le networking et l\'ambiance. Toujours en faire en double.' },
  { e: 'Programme non distribué', c: 'Un programme papier ou digital donne le ton et structure la journée.' },
  { e: 'Oublier les restrictions alimentaires', c: 'Demander systématiquement vegans, allergiques, halal/casher lors de l\'inscription.' },
  { e: 'Pas de retour d\'expérience', c: 'Un questionnaire post-événement améliore chaque édition et valorise les participants.' },
];

export default function GuideEntreprisePage() {
  const [checkItems, setCheckItems] = useState(
    CHECKLIST_PRO.flatMap(c => c.items.map(item => ({ label: item, cat: c.cat, done: false })))
  );
  const progress = Math.round(checkItems.filter(i => i.done).length / checkItems.length * 100);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ minHeight: '55vh' }}>
        <img src="https://images.unsplash.com/photo-1756885465373-bce82fd5c944?w=1400&q=85"
          alt="Événement entreprise" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(11,30,32,0.88) 0%, rgba(11,30,32,0.5) 60%, transparent 100%)' }} />
        <div className="relative container-ose flex flex-col justify-end pb-12" style={{ minHeight: '55vh', paddingTop: '5rem' }}>
          <nav className="flex items-center gap-2 text-xs mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
            <Link to="/guides" style={{ color: '#5DBCBF' }}>Guides</Link>
            <span>/</span>
            <span style={{ color: '#FFF' }}>Entreprise & CE</span>
          </nav>
          <span className="inline-block w-fit px-4 py-2 rounded-full text-xs font-bold mb-4"
            style={{ background: 'rgba(93,188,191,0.2)', color: '#5DBCBF', border: '1px solid rgba(93,188,191,0.4)' }}>
            💼 Guide événement professionnel
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-3"
            style={{ color: '#FFF', letterSpacing: '-0.03em', lineHeight: '1.05' }}>
            Organiser un événement d'entreprise mémorable
          </h1>
          <p className="text-sm mb-6 max-w-2xl" style={{ color: 'rgba(255,255,255,0.75)', lineHeight: '1.75' }}>
            Séminaire, cocktail, inauguration, team building, soirée CE. Checklist pro, planning, animations.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#checklist" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm"
              style={{ background: '#5DBCBF', color: '#0B1E20' }}>
              <Check size={16} /> Checklist organisateur
            </a>
            <Link to="/creer-evenement" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm"
              style={{ background: 'rgba(255,255,255,0.12)', color: '#FFF', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)' }}>
              Devis pro <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* PROGRESSION */}
      <div style={{ background: '#0B1E20', borderBottom: '1px solid rgba(93,188,191,0.2)' }}>
        <div className="container-ose py-3 flex items-center gap-4">
          <p className="text-xs font-semibold" style={{ color: '#5DBCBF' }}>Préparation : {progress}%</p>
          <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: '#5DBCBF' }} />
          </div>
        </div>
      </div>

      {/* TYPES D'ÉVÉNEMENTS PRO */}
      <section className="section-py section-white">
        <div className="container-ose">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)' }}>Quel type d'événement organisez-vous ?</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { emoji: '🥂', type: 'Cocktail d\'entreprise', desc: 'Inauguration, fin de chantier, soirée de fin d\'année. Ambiance détendue et conviviale.' },
              { emoji: '🤝', type: 'Team Building', desc: 'Cohésion d\'équipe, challenges, jeux de groupe. Renforcer les liens entre collaborateurs.' },
              { emoji: '🎤', type: 'Séminaire', desc: 'Conférence, présentation, assemblée générale. Technique et organisation au premier plan.' },
              { emoji: '🚀', type: 'Lancement produit', desc: 'Mise en avant, animation, showroom. Créer l\'événement autour de votre produit.' },
              { emoji: '📣', type: 'Animation commerciale', desc: 'En boutique, marché ou salon. Attirer les clients avec animation et bonne humeur.' },
              { emoji: '🏅', type: 'Soirée CE', desc: 'Repas, animations, récompenses. Valoriser vos collaborateurs et créer de la fierté.' },
            ].map((t, i) => (
              <div key={i} className="card-premium p-5">
                <div className="text-3xl mb-3">{t.emoji}</div>
                <p className="font-bold text-sm mb-1" style={{ color: 'var(--ink)' }}>{t.type}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHECKLIST */}
      <section id="checklist" className="section-py" style={{ background: 'var(--bg)' }}>
        <div className="container-ose">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)' }}>Checklist organisateur</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CHECKLIST_PRO.map(cat => {
              const catItems = checkItems.filter(i => i.cat === cat.cat);
              const done = catItems.filter(i => i.done).length;
              return (
                <div key={cat.cat} className="card-premium p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{cat.cat}</p>
                    <span className="text-xs font-bold" style={{ color: '#5DBCBF' }}>{done}/{catItems.length}</span>
                  </div>
                  {catItems.map(item => (
                    <button key={item.label} onClick={() => setCheckItems(prev => prev.map(i => i.label === item.label && i.cat === item.cat ? { ...i, done: !i.done } : i))}
                      className="w-full flex items-start gap-2 p-1.5 rounded-lg text-left mb-1"
                      style={{ background: item.done ? 'rgba(93,188,191,0.06)' : 'transparent', border: 'none', cursor: 'pointer' }}>
                      <div className="w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ borderColor: item.done ? '#5DBCBF' : 'var(--border)', background: item.done ? '#5DBCBF' : 'transparent' }}>
                        {item.done && <Check size={9} color="white" />}
                      </div>
                      <span className="text-xs" style={{ color: item.done ? 'var(--text-muted)' : 'var(--ink)', textDecoration: item.done ? 'line-through' : 'none' }}>{item.label}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ERREURS */}
      <section className="section-py section-white">
        <div className="container-ose">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)' }}>6 erreurs fréquentes en événementiel d'entreprise</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ERREURS_PRO.map((e, i) => (
              <div key={i} className="card-premium p-5">
                <AlertTriangle size={16} style={{ color: '#F59E0B', marginBottom: 8 }} />
                <p className="font-bold text-sm mb-1" style={{ color: 'var(--ink)' }}>{e.e}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>{e.c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-py" style={{ background: '#0B1E20' }}>
        <div className="container-ose text-center">
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#5DBCBF' }}>Tarifs CE jusqu'à -20%</p>
          <h2 className="font-display text-2xl font-bold mb-4" style={{ color: '#FFF' }}>Show Événement, votre partenaire événementiel pro</h2>
          <p className="text-sm mb-8 max-w-lg mx-auto" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Facturation professionnelle avec TVA. Disponible en semaine et week-end. Rayon 1h30 autour de Figeac.
          </p>
          <Link to="/creer-evenement"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold"
            style={{ background: '#5DBCBF', color: '#0B1E20', boxShadow: '0 12px 40px rgba(93,188,191,0.3)' }}>
            Demander un devis pro <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </div>
  );
}
